/**
 * Step Renderer Component - Phase 4 Implementation
 *
 * Renders the appropriate step component based on current step and layer.
 * Replaces the switch statement from RegisterAssetPage.tsx.
 */

import { Alert, Box } from '@mui/material';
import React from 'react';
import { LayerType } from '../../../types/layerMetadata';
import { LayerOption } from '../../../types/taxonomy.types';
import {
    Asset,
    FileUploadResponse,
    TaxonomySelection,
} from '../contexts/RegistrationContext';

// Import step components (these already exist)
import CompositeSelectionStep from '../../../components/asset/steps/CompositeSelectionStep';
import FileUploadStep from '../../../components/asset/steps/FileUploadStep';
import LayerSelectionStep from '../../../components/asset/steps/LayerSelectionStep';
import ReviewDetailsStep from '../../../components/asset/steps/ReviewDetailsStep';
import TaxonomySelectionStep from '../../../components/asset/steps/TaxonomySelectionStep';

// Metadata step
import { MetadataReviewStep } from './steps/MetadataReviewStep';

const noopValidationChange = () => {};

export interface StepRendererProps {
  currentStep: number;
  selectedLayer: LayerType | null;
  taxonomySelection: TaxonomySelection | null;
  layerMetadata: any;
  uploadedFiles: FileUploadResponse[];
  selectedComponents: Asset[];
  isSubmitting: boolean;
  formData: Record<string, any>;
  // Context actions (legacy)
  setSelectedLayer?: (layer: LayerType) => void;
  setTaxonomySelection?: (taxonomy: TaxonomySelection) => void;
  addUploadedFile?: (file: FileUploadResponse) => void;
  removeUploadedFile?: (fileName: string) => void;
  setIsVariant?: (isVariant: boolean) => void;
  setBaseAsset?: (asset: Asset | null) => void;
  // New callback system
  onLayerSelect?: (layer: LayerOption, isDoubleClick?: boolean) => void;
  onTaxonomyUpdate?: (field: string, value: any) => void;
  onSubcategorySelect?: (subcategoryCode: string) => void;
  // AI processing callbacks
  onAIResults?: (results: any) => void;
  onLayerMetadata?: (metadata: any) => void;
  // Form data update callback
  updateFormData?: (data: Record<string, any>) => void;
  // Submit callback
  onSubmit?: () => Promise<void>;
  // AI results (for Review step description fallback)
  aiResults?: any;
}

export const StepRenderer: React.FC<StepRendererProps> = ({
  currentStep,
  selectedLayer,
  taxonomySelection,
  layerMetadata,
  uploadedFiles,
  selectedComponents,
  isSubmitting,
  formData,
  // Context actions (legacy)
  setSelectedLayer,
  setTaxonomySelection,
  addUploadedFile,
  removeUploadedFile,
  setIsVariant,
  setBaseAsset,
  // New callback system
  onLayerSelect,
  onTaxonomyUpdate,
  onSubcategorySelect,
  // AI processing callbacks
  onAIResults,
  onLayerMetadata,
  // Form data update callback
  updateFormData,
  // Submit callback
  onSubmit,
  aiResults,
}) => {
  // Helper to determine if this is a composite layer that needs component selection
  const isCompositeLayer = ['C', 'T', 'R', 'P'].includes(selectedLayer || '');
  const isTrainingLayer = selectedLayer === 'T';

  const renderFileUploadStepContent = (): React.ReactNode => (
    <>
      <FileUploadStep
        register={(() => ({})) as any}
        watch={((field: string) => {
          if (field === 'layer') return selectedLayer;
          if (field === 'layerName') {
            const layerNames: Record<string, string> = {
              G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds',
              B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights'
            };
            return layerNames[selectedLayer || ''] || '';
          }
          if (field === 'categoryCode') return taxonomySelection?.category || formData?.categoryCode || '';
          if (field === 'categoryName') {
            const categoryName = taxonomySelection?.categoryName || formData?.categoryName;
            return typeof categoryName === 'string' ? categoryName : (categoryName?.name || categoryName?.displayName || taxonomySelection?.category || '');
          }
          if (field === 'subcategoryCode') return taxonomySelection?.subcategory || formData?.subcategoryCode || '';
          if (field === 'subcategoryName') {
            const subcategoryName = taxonomySelection?.subcategoryName || formData?.subcategoryName;
            return typeof subcategoryName === 'string' ? subcategoryName : (subcategoryName?.name || subcategoryName?.displayName || taxonomySelection?.subcategory || '');
          }
          return (formData as any)[field];
        }) as any}
        setValue={(field: string, value: any) => {
          if (updateFormData) updateFormData({ [field]: value });
          else Object.assign(formData, { [field]: value });
        }}
        getValues={((fieldName?: string | string[]) => {
          const getAllValues = () => ({
            ...formData,
            layer: selectedLayer,
            layerName: (() => { const m: Record<string, string> = { G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }; return m[selectedLayer ?? ''] ?? ''; })(),
            categoryCode: taxonomySelection?.category || formData?.categoryCode || '',
            categoryName: taxonomySelection?.categoryName || formData?.categoryName || '',
            subcategoryCode: taxonomySelection?.subcategory || formData?.subcategoryCode || '',
            subcategoryName: taxonomySelection?.subcategoryName || formData?.subcategoryName || '',
          });
          if (fieldName) {
            const allValues = getAllValues();
            if (typeof fieldName === 'string') return (allValues as any)[fieldName];
            if (Array.isArray(fieldName)) {
              const result: any = {};
              fieldName.forEach(name => { result[name] = (allValues as any)[name]; });
              return result;
            }
          }
          return getAllValues();
        }) as any}
        errors={{}}
        onFilesChange={(files: File[]) => {
          if (updateFormData) updateFormData({ files });
          else Object.assign(formData, { files });
          if (files.length === 0 && uploadedFiles.length > 0) {
            uploadedFiles.forEach(file => { if (removeUploadedFile) removeUploadedFile(file.fileName); });
          }
        }}
        onUploadProgress={() => {}}
        onUploadComplete={((fileId: string, response: any) => {
          if (addUploadedFile && response) addUploadedFile({ fileName: fileId, url: response.url || response.fileUrl, mimeType: response.mimeType, size: response.size, originalName: response.originalName || fileId, ...response });
          if (response?.aiResults) {
            if (response.aiResults.description) Object.assign(formData, { description: response.aiResults.description, tags: response.aiResults.tags || [] });
            if (response.aiResults.layerMetadata) Object.assign(formData, { layerMetadata: response.aiResults.layerMetadata });
          }
        }) as any}
        onUploadError={() => {}}
        onSourceChange={() => {}}
        onAIDescriptionGenerated={(d: string) => Object.assign(formData, { description: d })}
        onAITagsGenerated={(tags: string[]) => Object.assign(formData, { tags })}
        onAIAdditionalMetadataGenerated={(metadata: any) => {
          if (!metadata) return;
          const extracted = metadata.layerMetadata || metadata;
          const layerCodeToType: Record<string, string> = { G: 'songs', S: 'stars', L: 'looks', M: 'moves', W: 'worlds', C: 'composite' };
          let processed = { ...extracted };
          if (!processed.layerType && selectedLayer) processed.layerType = layerCodeToType[selectedLayer] || processed.layerType;
          const layerSpecificFieldMap: Record<string, string> = { songs: 'songMetadata', stars: 'starMetadata', looks: 'looksMetadata', moves: 'movesMetadata', worlds: 'worldsMetadata', composite: 'compositeMetadata' };
          const layerToField: Record<string, string> = { G: 'songMetadata', S: 'starMetadata', L: 'looksMetadata', M: 'movesMetadata', W: 'worldsMetadata', C: 'compositeMetadata', B: 'layerMetadata', P: 'layerMetadata', T: 'layerMetadata', R: 'layerMetadata' };
          const legacyField = (processed.layerType && layerSpecificFieldMap[processed.layerType]) || (selectedLayer && layerToField[selectedLayer]);
          const formUpdates: Record<string, any> = { layerMetadata: processed };
          if (legacyField) formUpdates[legacyField] = processed;
          if (updateFormData) updateFormData(formUpdates);
          if (onLayerMetadata) onLayerMetadata(processed);
          if (onAIResults) onAIResults({ layerMetadata: processed, description: formData.description || '', tags: formData.tags || [] });
        }}
        uploadedFiles={uploadedFiles as any}
        getLayerSpecificLabel={(layer: string) => ({ G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }[layer] ? `${({ G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }[layer])} Asset` : "Creator's Description")}
        getLayerSpecificPlaceholder={(layer: string) => ({ G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }[layer] ? `Describe your ${({ G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }[layer])} asset...` : 'Describe your asset...')}
        getLayerSpecificGuidance={(layer: string) => ({ G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }[layer] ? `Guidelines for ${({ G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds', B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights' }[layer])} layer` : 'Guidelines for this layer')}
        loading={false}
        triggerAIGeneration={(() => {}) as any}
      />
    </>
  );

  const renderStep = (): React.ReactNode => {
    // Composite (C) layer: 5 steps with Component Selection before File Upload
    if (selectedLayer === 'C') {
      switch (currentStep) {
        case 0:
          return (
            <LayerSelectionStep
              selectedLayerCode={selectedLayer || ''}
              onLayerSelect={(layer: LayerOption, isDoubleClick?: boolean) => {
                if (onLayerSelect) onLayerSelect(layer, isDoubleClick);
                else if (setSelectedLayer) setSelectedLayer(layer.code as LayerType);
              }}
            />
          );
        case 1:
          if (!selectedLayer) return <Alert severity="error">Please select a layer first</Alert>;
          return (
            <TaxonomySelectionStep
              selectedLayer={selectedLayer || ''}
              selectedCategoryCode={taxonomySelection?.category || formData?.categoryCode || ''}
              selectedSubcategoryCode={taxonomySelection?.subcategory || formData?.subcategoryCode || ''}
              setValue={(field: string, value: any) => { if (onTaxonomyUpdate) onTaxonomyUpdate(field, value); }}
              getValues={(() => {
                const getValuesWrapper = (fieldName?: any): any => {
                  if (fieldName === 'categoryName') return taxonomySelection?.category || formData?.categoryName || '';
                  if (fieldName === 'subcategoryName') return taxonomySelection?.subcategory || formData?.subcategoryName || '';
                  return fieldName ? (formData as any)[fieldName] ?? '' : formData as any;
                };
                return getValuesWrapper;
              })()}
              watch={((field: string) => (formData as any)[field]) as any}
              onSubcategorySelect={async (subcategoryCode: string) => {
                if (onSubcategorySelect) onSubcategorySelect(subcategoryCode);
                else if (setTaxonomySelection) setTaxonomySelection({
                  layer: selectedLayer!,
                  category: taxonomySelection?.category || formData?.categoryCode || '',
                  subcategory: subcategoryCode,
                  categoryName: taxonomySelection?.categoryName || formData?.categoryName || '',
                  subcategoryName: formData?.subcategoryName || '',
                });
              }}
              loading={false}
            />
          );
        case 2:
          return (
            <CompositeSelectionStep
              setValue={(field: string, value: any) => {
                if (updateFormData) {
                  updateFormData({ [field]: value });
                  if (field === 'layerSpecificData.components') updateFormData({ components: value });
                }
              }}
              getValues={() => formData as any}
              trigger={() => Promise.resolve(true)}
              handleSubmit={(fn: any) => fn}
              onBack={() => console.log('Back clicked')}
              onSubmit={onSubmit || (() => console.log('Submit clicked'))}
              onTriggerCompositeAI={() => console.log('Trigger composite AI')}
              compositeValidationErrors={[]}
              onValidationChange={noopValidationChange}
              isSubmitting={isSubmitting}
              loading={false}
              watchLayer={selectedLayer || ''}
              isCompositeLayer={true}
              resolvedLayer={selectedLayer ?? ''}
              resolvedCategoryCode={taxonomySelection?.category ?? ''}
              resolvedSubcategoryCode={taxonomySelection?.subcategory ?? ''}
            />
          );
        case 3:
          return renderFileUploadStepContent();
        case 4:
          const compositeReviewWatch = (field: string) => {
            if (field === 'name') return formData?.name ?? formData?.creatorDescription ?? '';
            if (field === 'description') return formData?.description ?? aiResults?.data?.description ?? aiResults?.description ?? '';
            return (formData as any)?.[field];
          };
          return (
            <ReviewDetailsStep
              register={(() => ({})) as any}
              watch={compositeReviewWatch as any}
              setValue={(field: string, value: any) => {
                if (updateFormData) updateFormData({ [field]: value });
              }}
              getValues={() => formData as any}
              errors={{}}
              onBack={() => console.log('Back clicked')}
              onSubmit={onSubmit || (() => console.log('Submit clicked'))}
              isSubmitting={isSubmitting}
              loading={false}
              uploadedFiles={uploadedFiles as any}
              getLayerSpecificPlaceholder={(layer: string) =>
                layer && layer !== 'undefined' && layer !== 'null' ? `Describe your ${layer} asset...` : 'Describe your asset...'}
              isCompositeLayer={true}
              isTrainingLayer={false}
            />
          );
        default:
          return <Alert severity="error">Invalid step: {currentStep}</Alert>;
      }
    }

    // Non-composite layers: original step order
    switch (currentStep) {
      case 0:
        // Step 1: Layer Selection
        return (
          <LayerSelectionStep
            selectedLayerCode={selectedLayer || ''}
            onLayerSelect={(layer: LayerOption, isDoubleClick?: boolean) => {
              console.log('Layer selected:', layer.code);

              // Use new callback system if available
              if (onLayerSelect) {
                onLayerSelect(layer, isDoubleClick);
              } else if (setSelectedLayer) {
                // Fall back to legacy context system
                setSelectedLayer(layer.code as LayerType);
              }
            }}
          />
        );

      case 1:
        // Step 2: Taxonomy Selection
        if (!selectedLayer) {
          return <Alert severity="error">Please select a layer first</Alert>;
        }

        return (
          <TaxonomySelectionStep
            selectedLayer={selectedLayer || ''}
            selectedCategoryCode={taxonomySelection?.category || formData?.categoryCode || ''}
            selectedSubcategoryCode={taxonomySelection?.subcategory || formData?.subcategoryCode || ''}
            setValue={(field: string, value: any) => {
              console.log(`📋 [STEP RENDERER] Taxonomy field updated: ${field} = ${value}`);
              console.log(`📋 [STEP RENDERER] Current taxonomySelection:`, taxonomySelection);
              console.log(`📋 [STEP RENDERER] Current formData:`, formData);

              // Use new callback system if available
              if (onTaxonomyUpdate) {
                onTaxonomyUpdate(field, value);
              }
              // Legacy system doesn't need setValue fallback since it's handled by watch/getValues
            }}
            getValues={(() => {
              const getValuesWrapper = (fieldName?: any): any => {
                if (fieldName && typeof fieldName === 'string') {
                  // Return specific field value, with fallbacks for name fields
                  if (fieldName === 'categoryName') {
                    return taxonomySelection?.category || formData?.categoryName || '';
                  }
                  if (fieldName === 'subcategoryName') {
                    return taxonomySelection?.subcategory || formData?.subcategoryName || '';
                  }
                  return (formData as any)[fieldName] || '';
                }
                return formData as any;
              };
              return getValuesWrapper;
            })()}
            watch={((field: string) => (formData as any)[field]) as any}
            onSubcategorySelect={async (subcategoryCode: string) => {
              console.log('Subcategory selected:', subcategoryCode);

              // Use new callback system if available
              if (onSubcategorySelect) {
                onSubcategorySelect(subcategoryCode);
              } else if (setTaxonomySelection) {
                // Fall back to legacy context system
                setTaxonomySelection({
                  layer: selectedLayer!,
                  category: taxonomySelection?.category || formData?.categoryCode || '',
                  subcategory: subcategoryCode,
                  categoryName: taxonomySelection?.categoryName || formData?.categoryName || '',
                  subcategoryName: formData?.subcategoryName || '',
                });
              }
            }}
            loading={false}
          />
        );

      case 2:
        // Step 3: File Upload
        return (
          <>
            <FileUploadStep
              register={(() => ({})) as any} // Will be replaced with context integration
              // NOTE: Using one-step backend integration via submitAsset
              watch={((field: string) => {
                // Provide proper layer and taxonomy data from context
                if (field === 'layer') return selectedLayer;
                if (field === 'layerName') {
                  const layerNames: Record<string, string> = {
                    G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds',
                    B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights'
                  };
                  return layerNames[selectedLayer || ''] || '';
                }
                if (field === 'categoryCode') return taxonomySelection?.category || formData?.categoryCode || '';
                if (field === 'categoryName') {
                  // Extract string value from potentially nested object
                  const categoryName = taxonomySelection?.categoryName || formData?.categoryName;
                  return typeof categoryName === 'string' ? categoryName : (categoryName?.name || categoryName?.displayName || taxonomySelection?.category || '');
                }
                if (field === 'subcategoryCode') return taxonomySelection?.subcategory || formData?.subcategoryCode || '';
                if (field === 'subcategoryName') {
                  // Extract string value from potentially nested object
                  const subcategoryName = taxonomySelection?.subcategoryName || formData?.subcategoryName;
                  return typeof subcategoryName === 'string' ? subcategoryName : (subcategoryName?.name || subcategoryName?.displayName || taxonomySelection?.subcategory || '');
                }
                return (formData as any)[field];
              }) as any}
              setValue={(field: string, value: any, options?: any) => {
                console.log('🔧 [SET VALUE] File upload field updated:', field, value);
                // CRITICAL FIX: Actually update formData so watch() can read the new values
                if (updateFormData) {
                  updateFormData({ [field]: value });
                } else {
                  // Fallback: mutate formData directly (won't trigger re-render but better than nothing)
                  Object.assign(formData, { [field]: value });
                  console.warn('⚠️ [SET VALUE] updateFormData not available, using direct mutation');
                }
              }}
              getValues={((fieldName?: string | string[]) => {
                // Helper function to build the full form values object
                const getAllValues = () => ({
                  ...formData,
                  layer: selectedLayer,
                  layerName: (() => {
                    const layerNames: Record<string, string> = {
                      G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds',
                      B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights'
                    };
                    return layerNames[selectedLayer || ''] || '';
                  })(),
                  categoryCode: taxonomySelection?.category || formData?.categoryCode || '',
                  categoryName: (() => {
                    // Extract string value from potentially nested object
                    const categoryName = taxonomySelection?.categoryName || formData?.categoryName;
                    return typeof categoryName === 'string' ? categoryName : (categoryName?.name || categoryName?.displayName || taxonomySelection?.category || '');
                  })(),
                  subcategoryCode: taxonomySelection?.subcategory || formData?.subcategoryCode || '',
                  subcategoryName: (() => {
                    // Extract string value from potentially nested object
                    const subcategoryName = taxonomySelection?.subcategoryName || formData?.subcategoryName;
                    return typeof subcategoryName === 'string' ? subcategoryName : (subcategoryName?.name || subcategoryName?.displayName || taxonomySelection?.subcategory || '');
                  })(),
                });

                // If fieldName is provided, return that specific field's value
                if (fieldName) {
                  const allValues = getAllValues();
                  // Handle both string and array cases
                  if (typeof fieldName === 'string') {
                    return (allValues as any)[fieldName];
                  } else if (Array.isArray(fieldName)) {
                    // Return object with requested fields
                    const result: any = {};
                    fieldName.forEach(name => {
                      result[name] = (allValues as any)[name];
                    });
                    return result;
                  }
                }

                // If no fieldName provided, return all values
                return getAllValues();
              }) as any}
              errors={{}}
              onFilesChange={(files: File[]) => {
                console.log('🔧 [FILES CHANGE] Files changed:', files.length, 'files');

                // CRITICAL FIX: Store files in formData so they persist across steps
                if (updateFormData) {
                  updateFormData({ files: files });
                  console.log('✅ [FILES CHANGE] Stored files in formData via updateFormData');
                } else {
                  // Fallback: mutate formData directly (won't trigger re-render but better than nothing)
                  Object.assign(formData, { files: files });
                  console.warn('⚠️ [FILES CHANGE] updateFormData not available, using direct mutation');
                }

                // Clear uploaded files from context when files are removed
                if (files.length === 0 && uploadedFiles.length > 0) {
                  // Clear all uploaded files if no files selected
                  uploadedFiles.forEach(file => {
                    if (removeUploadedFile) {
                      removeUploadedFile(file.fileName);
                    }
                  });
                }
              }}
              onUploadProgress={(fileId: string, progress: number) => {
                console.log('Upload progress:', fileId, progress);
              }}
              onUploadComplete={
                ((fileId: string, response: any) => {
                  console.log('Upload complete:', fileId, response);

                  // Add uploaded file to context state
                  if (addUploadedFile && response) {
                    addUploadedFile({
                      fileName: fileId,
                      url: response.url || response.fileUrl,
                      mimeType: response.mimeType,
                      size: response.size,
                      originalName: response.originalName || fileId,
                      ...response
                    });
                  }

                  // If this response contains AI results, set them in the context
                  if (response && response.aiResults) {
                    console.log('🤖 [AI RESULTS] Setting AI results from upload response:', response.aiResults);

                    // Update form data with AI-generated content
                    if (response.aiResults.description) {
                      Object.assign(formData, {
                        description: response.aiResults.description,
                        tags: response.aiResults.tags || [],
                      });
                    }

                    // Set layer metadata if available
                    if (response.aiResults.layerMetadata) {
                      console.log('🎯 [LAYER METADATA] Setting layer metadata from AI results:', response.aiResults.layerMetadata);
                      Object.assign(formData, {
                        layerMetadata: response.aiResults.layerMetadata
                      });
                    }
                  }
                }) as any
              }
              onUploadError={(error: string) => {
                console.log('Upload error:', error);
              }}
              onSourceChange={(source: string) => {
                console.log('Source changed:', source);
              }}
              onAIDescriptionGenerated={(description: string) => {
                console.log('AI description generated:', description);
                // Update form data with generated description
                Object.assign(formData, { description });
              }}
              onAITagsGenerated={(tags: string[]) => {
                console.log('AI tags generated:', tags);
                // Update form data with generated tags
                Object.assign(formData, { tags });
              }}
              onAIAdditionalMetadataGenerated={(metadata: any) => {
                console.log('🎯 [AI METADATA] AI metadata generated:', metadata);

                if (!metadata) {
                  console.warn('⚠️ [AI METADATA] No metadata received');
                  return;
                }

                // CRITICAL FIX: Extract layerMetadata from metadata object
                // The metadata object contains: { layerMetadata: {...}, albumArtUrl, etc. }
                const extractedLayerMetadata = metadata.layerMetadata || metadata;
                console.log('🎯 [AI METADATA] Extracted layerMetadata:', extractedLayerMetadata);

                if (!extractedLayerMetadata) {
                  console.warn('⚠️ [AI METADATA] No layerMetadata found in metadata object');
                  return;
                }

                // CRITICAL FIX: Map layer codes to layerType if layerType is missing
                const layerCodeToTypeMap: Record<string, string> = {
                  G: 'songs',
                  S: 'stars',
                  L: 'looks',
                  M: 'moves',
                  W: 'worlds',
                  C: 'composite',
                };

                // Ensure layerType is set
                let processedLayerMetadata = { ...extractedLayerMetadata };
                if (!processedLayerMetadata.layerType && selectedLayer) {
                  const mappedLayerType = layerCodeToTypeMap[selectedLayer];
                  if (mappedLayerType) {
                    processedLayerMetadata.layerType = mappedLayerType;
                    console.log(
                      `🔧 [LAYER TYPE FIX] Set layerType from layer code: ${selectedLayer} → ${mappedLayerType}`
                    );
                  }
                }

                // Map layerType to form field names
                const layerSpecificFieldMap: Record<string, string> = {
                  songs: 'songMetadata',
                  stars: 'starMetadata',
                  looks: 'looksMetadata',
                  moves: 'movesMetadata',
                  worlds: 'worldsMetadata',
                  composite: 'compositeMetadata',
                };

                const detectedLayerType = processedLayerMetadata.layerType;
                let legacyField = detectedLayerType
                  ? layerSpecificFieldMap[detectedLayerType]
                  : undefined;

                // FALLBACK: If layerType detection failed, use layer code directly
                if (!legacyField && selectedLayer) {
                  const layerCodeFieldMap: Record<string, string> = {
                    G: 'songMetadata',
                    S: 'starMetadata',
                    L: 'looksMetadata',
                    M: 'movesMetadata',
                    W: 'worldsMetadata',
                    C: 'compositeMetadata',
                  };
                  legacyField = layerCodeFieldMap[selectedLayer];
                  if (legacyField) {
                    console.log(
                      `🔧 [FIELD MAPPING FALLBACK] Mapped layer code ${selectedLayer} to field ${legacyField}`
                    );
                  }
                }

                console.log('🔍 [FIELD MAPPING DEBUG] Field mapping:', {
                  detectedLayerType,
                  legacyField,
                  selectedLayer,
                  willSetLayerSpecificField: !!legacyField,
                });

                // Prepare form data updates
                const formUpdates: Record<string, any> = {
                  layerMetadata: processedLayerMetadata,
                };

                // Set layer-specific field (e.g., starMetadata for S layer)
                if (legacyField) {
                  formUpdates[legacyField] = processedLayerMetadata;
                  console.log(
                    `🔄 [V2.0 LEGACY] Will set ${legacyField} field for layerType: ${detectedLayerType || selectedLayer}`
                  );
                } else {
                  console.warn(
                    `⚠️ [FIELD MAPPING] Could not determine layer-specific field for layer: ${selectedLayer}, layerType: ${detectedLayerType}. Metadata may not be fully populated.`
                  );
                }

                // Update form data using updateFormData if available (triggers re-render)
                if (updateFormData) {
                  updateFormData(formUpdates);
                  console.log('✅ [AI METADATA] Updated form data via updateFormData:', formUpdates);
                } else {
                  // Fallback: mutate formData directly (won't trigger re-render)
                  Object.assign(formData, formUpdates);
                  console.warn('⚠️ [AI METADATA] updateFormData not available, using direct mutation');
                }

                // Also call the context callback to set metadata properly
                if (onLayerMetadata) {
                  onLayerMetadata(processedLayerMetadata);
                }

                // Set AI results in context
                if (onAIResults) {
                  onAIResults({
                    layerMetadata: processedLayerMetadata,
                    description: formData.description || '',
                    tags: formData.tags || [],
                  });
                }

                console.log('🎯 [AI METADATA] Updated form data and context with metadata');
              }}
              uploadedFiles={uploadedFiles as any}
              getLayerSpecificLabel={(layer: string) => {
                const layerNames: Record<string, string> = {
                  G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds',
                  B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights'
                };
                const layerName = layerNames[layer] || layer || '';
                return (layerName ? `${layerName} Asset` : "Creator's Description");
              }}
              getLayerSpecificPlaceholder={(layer: string) => {
                const layerNames: Record<string, string> = {
                  G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds',
                  B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights'
                };
                const layerName = layerNames[layer] || '';
                return `Provide a complement description of your ${layerName} to help AI extract the best results`
              }}
              getLayerSpecificGuidance={(layer: string) => {
                const layerNames: Record<string, string> = {
                  G: 'Songs', S: 'Stars', L: 'Looks', M: 'Moves', W: 'Worlds',
                  B: 'Branded', P: 'Personalize', T: 'Training Data', C: 'Composite', R: 'Rights'
                };
                const layerName = layerNames[layer] || '';
                return layerName ? `Guidelines for ${layerName} layer` : 'Guidelines for this layer';
              }}
              loading={false}
              triggerAIGeneration={
                (() => {
                  console.log('Triggering AI generation');
                }) as any
              }
            />
          </>
        );

      case 3:
        // Step 4: Metadata Review (was Step 5)
        // CRITICAL FIX: Use formData metadata as source of truth, with fallback to prop
        const getMetadataForReview = () => {
          // Priority 1: Use layer-specific metadata from formData (e.g., starMetadata for S layer)
          if (selectedLayer === 'S' && formData.starMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.starMetadata');
            return formData.starMetadata;
          }
          if (selectedLayer === 'G' && formData.songMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.songMetadata');
            return formData.songMetadata;
          }
          if (selectedLayer === 'L' && formData.looksMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.looksMetadata');
            return formData.looksMetadata;
          }
          if (selectedLayer === 'M' && formData.movesMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.movesMetadata');
            return formData.movesMetadata;
          }
          if (selectedLayer === 'W' && formData.worldsMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.worldsMetadata');
            return formData.worldsMetadata;
          }
          if ((selectedLayer as string) === 'C' && formData.compositeMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.compositeMetadata');
            return formData.compositeMetadata;
          }
          // Priority 2: Use layerMetadata from formData
          if (formData.layerMetadata) {
            console.log('🔍 [METADATA REVIEW] Using formData.layerMetadata');
            return formData.layerMetadata;
          }
          // Priority 3: Extract from new API structure (data.layerMetadata)
          // 🎵 SONGS LAYER FIX: Handle new API response structure
          if ((formData as any).aiMetadata?.data?.layerMetadata) {
            console.log('🔍 [METADATA REVIEW] Using aiMetadata.data.layerMetadata (new API structure)');
            return (formData as any).aiMetadata.data.layerMetadata;
          }
          // Priority 4: Fallback to layerMetadata prop
          if (layerMetadata) {
            console.log('🔍 [METADATA REVIEW] Using layerMetadata prop');
            return layerMetadata;
          }
          console.warn('⚠️ [METADATA REVIEW] No metadata found in formData or props');
          return null;
        };

        return (
          <MetadataReviewStep
            selectedLayer={selectedLayer}
            layerMetadata={getMetadataForReview()}
            isEditable={true}
            onMetadataChange={(metadata: any) => {
              console.log('🔧 [METADATA REVIEW] Metadata changed:', metadata);
              // Update formData when metadata is edited
              if (updateFormData) {
                const updates: Record<string, any> = { layerMetadata: metadata };
                // Also update layer-specific field
                if (selectedLayer === 'S') {
                  updates.starMetadata = metadata;
                } else if (selectedLayer === 'G') {
                  updates.songMetadata = metadata;
                } else if (selectedLayer === 'L') {
                  updates.looksMetadata = metadata;
                } else if (selectedLayer === 'M') {
                  updates.movesMetadata = metadata;
                } else if (selectedLayer === 'W') {
                  updates.worldsMetadata = metadata;
                } else if ((selectedLayer as string) === 'C') {
                  updates.compositeMetadata = metadata;
                }
                updateFormData(updates);
                console.log('✅ [METADATA REVIEW] Updated formData with metadata changes');
              }
            }}
          />
        );

      case 4:
        // Step 5: Review Details OR Component Selection
        if (isCompositeLayer) {
          // For composite layers, this is component selection
          return (
            <CompositeSelectionStep
              setValue={(field: string, value: any) => {
                console.log('Composite field updated:', field, value);
              }}
              getValues={() => formData as any}
              trigger={() => Promise.resolve(true)}
              handleSubmit={(fn: any) => fn}
              onBack={() => console.log('Back clicked')}
              onSubmit={onSubmit || (() => console.log('Submit clicked'))}
              onTriggerCompositeAI={() => console.log('Trigger composite AI')}
              compositeValidationErrors={[]}
              onValidationChange={(errors: string[]) => {
                console.log('Validation errors:', errors);
              }}
              isSubmitting={isSubmitting}
              loading={false}
              watchLayer={selectedLayer || ''}
              isCompositeLayer={isCompositeLayer}
            />
          );
        } else {
          // Final review for non-composite layers
          const reviewWatch = (field: string) => {
            if (field === 'name') return formData?.name ?? formData?.creatorDescription ?? '';
            if (field === 'description') return formData?.description ?? aiResults?.data?.description ?? aiResults?.description ?? '';
            return (formData as any)?.[field];
          };
          return (
            <ReviewDetailsStep
              register={(() => ({})) as any}
              watch={reviewWatch as any}
              setValue={(field: string, value: any, options?: any) => {
                console.log('🔧 [SET VALUE] Review field updated:', field, value);
                // CRITICAL FIX: Actually update formData so watch() can read the new values
                if (updateFormData) {
                  updateFormData({ [field]: value });
                } else {
                  // Fallback: mutate formData directly (won't trigger re-render but better than nothing)
                  Object.assign(formData, { [field]: value });
                  console.warn('⚠️ [SET VALUE] updateFormData not available, using direct mutation');
                }
              }}
              getValues={() => formData as any}
              errors={{}}
              onBack={() => console.log('Back clicked')}
              onSubmit={onSubmit || (() => console.log('Submit clicked'))}
              isSubmitting={isSubmitting}
              loading={false}
              uploadedFiles={uploadedFiles as any}
              getLayerSpecificPlaceholder={(layer: string) =>
                layer && layer !== 'undefined' && layer !== 'null'
                  ? `Describe your ${layer} asset...`
                  : 'Describe your asset...'
              }
              isCompositeLayer={false}
              isTrainingLayer={isTrainingLayer}
            />
          );
        }

      case 5:
        // Step 6: Final Review (Composite layers only)
        const finalReviewWatch = (field: string) => {
          if (field === 'name') return formData?.name ?? formData?.creatorDescription ?? '';
          if (field === 'description') return formData?.description ?? aiResults?.data?.description ?? aiResults?.description ?? '';
          return (formData as any)?.[field];
        };
        return (
          <ReviewDetailsStep
            register={(() => ({})) as any}
            watch={finalReviewWatch as any}
            setValue={(field: string, value: any, options?: any) => {
              console.log('🔧 [SET VALUE] Final review field updated:', field, value);
              // CRITICAL FIX: Actually update formData so watch() can read the new values
              if (updateFormData) {
                updateFormData({ [field]: value });
              } else {
                // Fallback: mutate formData directly (won't trigger re-render but better than nothing)
                Object.assign(formData, { [field]: value });
                console.warn('⚠️ [SET VALUE] updateFormData not available, using direct mutation');
              }
            }}
            getValues={() => formData as any}
            errors={{}}
            onBack={() => console.log('Back clicked')}
            onSubmit={onSubmit || (() => console.log('Submit clicked'))}
            isSubmitting={isSubmitting}
            loading={false}
            uploadedFiles={uploadedFiles as any}
            getLayerSpecificPlaceholder={(layer: string) =>
              layer && layer !== 'undefined' && layer !== 'null'
                ? `Describe your ${layer} asset...`
                : 'Describe your asset...'
            }
            isCompositeLayer={isCompositeLayer}
            isTrainingLayer={isTrainingLayer}
          />
        );

      default:
        return <Alert severity="error">Invalid step: {currentStep}</Alert>;
    }
  };

  return <Box sx={{ minHeight: 400 }}>{renderStep()}</Box>;
};

export default StepRenderer;
